import { useState } from "react";

import Button from "@/design-system/button";
import { ArrowLeft } from "@/design-system/icons/arrow-left";
import { CloseIconSlimButton } from "@/design-system/icons/close-icon-slim";
import Magnifier from "@/design-system/icons/magnifier.svg";
import TelemetryButton from "@/design-system/telemetry-button";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector } from "@/src/contexts";
import { useOnSearchFormSubmit, useOnSearchInputChange } from "@/src/shared/Header/header-search-helper";
import HeaderSearchResults from "@/src/shared/Header/header-search-results";
import useSearchHelper from "@/src/shared/Header/hooks/use-search-helper";
import ModalV2 from "@Components/ModalV2";
import { TelemetryEntity } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, colors } from "../../../styles/tokens.stylex";
import { SearchBarResources } from "./SearchBar.Resources";
import { SearchBarResourcesEn } from "./SearchBar.Resources.En";

export default function HeaderSearchMobile() {
    const resources = languageSelector(SearchBarResources, SearchBarResourcesEn);
    const [showResults, setShowResults] = useState(false);
    const { inputValue, setSearchText, inputRef } = useOnSearchInputChange();
    useSearchHelper(setShowResults, inputRef);

    const onClose = () => setShowResults(false);

    const onSubmit = useOnSearchFormSubmit();

    const showClear = inputValue.length > 0;

    return (
        <>
            <Button
                as="button"
                variant="none"
                size="None"
                onClick={() => setShowResults(true)}
                styleX={styles.button}
            >
                <img src={Magnifier} alt={resources.searchWatermark} />
            </Button>
            {showResults && (
                <ModalV2
                    onClose={onClose}
                    header={
                        <TelemetryButton
                            as="button"
                            variant="none"
                            size="None"
                            eventName="common-back"
                            onClick={onClose}
                            styleX={styles.arrowBack}
                        >
                            <ArrowLeft />
                        </TelemetryButton>
                    }
                    headerStyleX={styles.customHeader}
                    mainStyleX={styles.customMain}
                    closeButtonStyleX={styles.modalIconClose}
                >
                    <form {...stylex.props(styles.form)} onSubmit={onSubmit}>
                        <img {...stylex.props(styles.searchIcon)} src={Magnifier} alt="Szukaj w goodie" />
                        <input
                            {...stylex.props(styles.input)}
                            ref={inputRef}
                            type="text"
                            enterKeyHint="search"
                            tabIndex={1}
                            defaultValue={inputValue}
                            onFocus={() => setShowResults(true)}
                            placeholder={resources.searchWatermark}
                            onChange={(ev) => setSearchText(ev.currentTarget.value)}
                            autoFocus
                            data-testid="hdr-srchbar"
                        />

                        <CloseIconSlimButton
                            type="button"
                            styleX={[styles.clearBtn, showClear && styles.showClear]}
                            onClick={() => {
                                if (inputRef.current) {
                                    inputRef.current.value = "";
                                    setSearchText("");
                                    requestAnimationFrame(() => inputRef.current.focus());
                                }

                                sendTelemetryEvent({
                                    eventName: `${TelemetryEntity.Search}-keywordsClear`,
                                    origin: "searchResultsGlobal",
                                    actionType: "Click",
                                });
                            }}
                            aria-hidden={!showClear}
                        />
                    </form>
                    <div {...stylex.props(styles.searchResult)}>
                        <HeaderSearchResults
                            isMobile
                            onClick={(value) => {
                                setSearchText(value);
                            }}
                            text={inputValue}
                        />
                    </div>
                </ModalV2>
            )}
        </>
    );
}

const styles = stylex.create({
    form: {
        position: "relative",
        marginBottom: ComposableSpacing.s6,
    },

    button: {
        height: "2.5em",

        paddingBottom: "0.625em",
        paddingLeft: "0.625em",
        paddingRight: "0.625em",
        paddingTop: "0.625em",
        width: "2.5em",
    },

    searchIcon: {
        pointerEvents: "none",
        position: "absolute",
        zIndex: 0,
        height: "1.25em",
        left: "0.625em",
        top: "0.5em",
        width: "1.25em",
    },

    input: {
        borderRadius: "1.25em",
        borderStyle: "none",

        outline: "none",
        backgroundColor: "#efefef",

        backgroundImage: "none",
        paddingBottom: ".625em",
        paddingLeft: "2.5em",
        paddingRight: "2em",
        paddingTop: ".625em",
        width: "100%",
    },

    clearBtn: {
        color: colors.fg_BrandNavy,

        lineHeight: 1,
        opacity: 0,
        position: "absolute",

        transitionDuration: "0.25s",

        transitionProperty: "opacity",

        transitionTimingFunction: "ease-in-out",
        zIndex: 0,
        height: "0.8125em",
        right: "0.625em",
        top: "0.79em",
        width: "0.8125em",
    },

    showClear: {
        opacity: 1,
    },

    arrowBack: {
        strokeWidth: 1,
        margin: 0,
        color: colors.fg_BrandNavy,
        height: "2rem",
        width: "2rem",
    },
    modalIconClose: {
        top: "1.25rem",
    },
    customHeader: {
        paddingBottom: ComposableSpacing.s2,
        paddingLeft: ComposableSpacing.s4,
        paddingRight: ComposableSpacing.s4,
        paddingTop: ComposableSpacing.s3,
    },
    customMain: {
        paddingLeft: ComposableSpacing.s4,
        paddingRight: ComposableSpacing.s4,
    },

    searchResult: {
        gap: ComposableSpacing.s5,
        display: "flex",
        flexDirection: "column",
    },
});
